import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../store'
import { logout } from '../../store/slices/authSlice'
import {
  Bell, Briefcase, Home, MessageSquare, Users,
  Wallet, LogOut, Settings, Search, Link2, ChevronDown,
} from 'lucide-react'
import { useState, useRef, useEffect } from 'react'

export default function Layout() {
  const dispatch   = useAppDispatch()
  const navigate   = useNavigate()
  const location   = useLocation()
  const { isAuthenticated, user } = useAppSelector((s) => s.auth)
  const { unreadCount }           = useAppSelector((s) => s.notifications)
  const { balance }               = useAppSelector((s) => s.wallet)
  const balanceRupees             = (balance ?? 0) / 100
  const [menuOpen, setMenuOpen]   = useState(false)
  const menuRef                   = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const handleLogout = () => {
    dispatch(logout())
    navigate('/login')
    setMenuOpen(false)
  }

  const isActive = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path)

  const navTabs = [
    { to: '/',              icon: Home,          label: 'Home'      },
    { to: '/connections',   icon: Users,         label: 'Network'   },
    { to: '/chat',          icon: MessageSquare, label: 'Messages'  },
    { to: '/my-applications', icon: Briefcase,   label: 'Jobs'      },
    ...(user?.roles?.includes('REFERRER')
      ? [{ to: '/my-referrals', icon: Link2, label: 'Referrals' }]
      : []),
  ]

  const avatarInitial = (user?.displayName || user?.email || 'U')[0].toUpperCase()

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FDFAFF' }}>

      {/* ── Navbar ── */}
      <nav className="bg-white sticky top-0 z-40" style={{ borderBottom: '1px solid #EDE9FE', boxShadow: '0 2px 12px rgba(139,92,246,0.06)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center h-14">

          {/* Left: Logo + Search */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <Link to="/" className="no-underline flex-shrink-0">
              <div
                className="w-9 h-9 rounded-xl flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)', boxShadow: '0 3px 10px rgba(139,92,246,0.4)' }}
              >
                <span className="text-white font-bold text-lg leading-none select-none">R</span>
              </div>
            </Link>
            <div className="relative hidden md:block">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: '#A89EC0' }} />
              <input
                type="text"
                placeholder="Search"
                className="pl-9 pr-3 py-1.5 text-sm w-44 rounded-lg outline-none transition-all"
                style={{
                  background: '#F5F3FF',
                  border: '1.5px solid #EDE9FE',
                  color: '#13082A',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#8B5CF6'
                  e.target.style.background  = '#FFFFFF'
                  e.target.style.boxShadow   = '0 0 0 3px rgba(139,92,246,0.10)'
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#EDE9FE'
                  e.target.style.background  = '#F5F3FF'
                  e.target.style.boxShadow   = 'none'
                }}
              />
            </div>
          </div>

          {/* Center: Nav tabs */}
          <div className="flex-1 flex items-end justify-center h-14 overflow-x-auto">
            {isAuthenticated ? (
              navTabs.map(({ to, icon: Icon, label }) => (
                <Link
                  key={to}
                  to={to}
                  className={`nav-tab ${isActive(to) ? 'nav-tab-active' : 'nav-tab-inactive'}`}
                >
                  <Icon size={20} />
                  <span className="text-xs font-semibold whitespace-nowrap">{label}</span>
                </Link>
              ))
            ) : (
              <span className="text-sm font-semibold tracking-wide" style={{ color: '#8B5CF6' }}>
                Professional Job Referral Network
              </span>
            )}
          </div>

          {/* Right: Wallet · Bell · Me */}
          <div className="flex items-end h-14 flex-shrink-0">
            {isAuthenticated ? (
              <>
                {/* Wallet */}
                <Link
                  to="/wallet"
                  className={`nav-tab hidden lg:flex ${isActive('/wallet') ? 'nav-tab-active' : 'nav-tab-inactive'}`}
                >
                  <Wallet size={20} />
                  <span className="text-xs font-semibold">₹{balanceRupees.toFixed(0)}</span>
                </Link>

                {/* Notifications */}
                <Link
                  to="/notifications"
                  className={`nav-tab ${isActive('/notifications') ? 'nav-tab-active' : 'nav-tab-inactive'}`}
                >
                  <div className="relative">
                    <Bell size={20} />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1.5 -right-1.5 w-4 h-4 text-white text-[10px] rounded-full flex items-center justify-center font-bold leading-none" style={{ background: '#DC2626' }}>
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </div>
                  <span className="text-xs font-semibold">Alerts</span>
                </Link>

                {/* Me dropdown */}
                <div className="relative" ref={menuRef}>
                  <button
                    onClick={() => setMenuOpen(!menuOpen)}
                    className={`nav-tab border-none ${menuOpen ? 'nav-tab-active' : 'nav-tab-inactive'}`}
                    style={{ background: 'none' }}
                  >
                    <div className="w-7 h-7 rounded-full overflow-hidden flex items-center justify-center ring-2 ring-violet-300" style={{ background: '#EDE9FE' }}>
                      {(user as any)?.profile?.profilePicUrl ? (
                        <img src={(user as any).profile.profilePicUrl} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-xs font-bold" style={{ color: '#7C3AED' }}>{avatarInitial}</span>
                      )}
                    </div>
                    <span className="text-xs font-semibold flex items-center gap-0.5">
                      Me <ChevronDown size={11} />
                    </span>
                  </button>

                  {menuOpen && (
                    <div
                      className="absolute right-0 top-full mt-2 w-64 bg-white rounded-2xl py-1 z-50 overflow-hidden"
                      style={{ border: '1px solid #EDE9FE', boxShadow: '0 12px 40px rgba(139,92,246,0.18), 0 0 0 1px #EDE9FE' }}
                    >
                      {/* Profile preview */}
                      <div className="px-4 py-4" style={{ borderBottom: '1px solid #EDE9FE' }}>
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-12 h-12 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0 ring-2 ring-violet-300" style={{ background: '#EDE9FE' }}>
                            {(user as any)?.profile?.profilePicUrl ? (
                              <img src={(user as any).profile.profilePicUrl} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <span className="text-xl font-bold" style={{ color: '#7C3AED' }}>{avatarInitial}</span>
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold truncate" style={{ color: '#13082A' }}>
                              {user?.displayName ?? user?.email}
                            </p>
                            <p className="text-xs truncate" style={{ color: '#6B6080' }}>{user?.activeRole}</p>
                          </div>
                        </div>
                        <Link
                          to="/profile"
                          className="block text-center text-sm font-semibold rounded-full py-1.5 no-underline transition-all"
                          style={{ color: '#7C3AED', border: '1.5px solid #C4B5FD' }}
                          onMouseEnter={(e) => { (e.target as HTMLElement).style.background = '#F5F3FF' }}
                          onMouseLeave={(e) => { (e.target as HTMLElement).style.background = 'transparent' }}
                          onClick={() => setMenuOpen(false)}
                        >
                          View Profile
                        </Link>
                      </div>

                      <div className="py-1">
                        <p className="px-4 pt-2 pb-1 text-xs font-semibold uppercase tracking-widest" style={{ color: '#A89EC0' }}>Account</p>
                        <Link
                          to="/wallet"
                          className="flex items-center gap-2.5 px-4 py-2.5 text-sm no-underline transition-colors"
                          style={{ color: '#6B6080' }}
                          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = '#F5F3FF' }}
                          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent' }}
                          onClick={() => setMenuOpen(false)}
                        >
                          <Wallet size={15} style={{ color: '#A89EC0' }} />
                          Wallet · <span className="font-semibold" style={{ color: '#7C3AED' }}>₹{balanceRupees.toFixed(2)}</span>
                        </Link>
                        {(user?.roles?.includes('ADMIN') || user?.roles?.includes('SUPER_ADMIN')) && (
                          <Link
                            to="/admin"
                            className="flex items-center gap-2.5 px-4 py-2.5 text-sm no-underline transition-colors"
                            style={{ color: '#6B6080' }}
                            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = '#F5F3FF' }}
                            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent' }}
                            onClick={() => setMenuOpen(false)}
                          >
                            <Settings size={15} style={{ color: '#A89EC0' }} /> Admin Panel
                          </Link>
                        )}
                      </div>

                      <div className="py-1" style={{ borderTop: '1px solid #EDE9FE' }}>
                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors"
                          style={{ color: '#6B6080' }}
                          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = '#FEE2E2'; (e.currentTarget as HTMLElement).style.color = '#DC2626' }}
                          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; (e.currentTarget as HTMLElement).style.color = '#6B6080' }}
                        >
                          <LogOut size={15} /> Sign Out
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="flex items-center gap-2 pb-2">
                <Link to="/login" className="text-sm font-semibold no-underline px-3 py-1.5 rounded-full transition-colors" style={{ color: '#6B6080' }}>
                  Sign in
                </Link>
                <Link to="/register" className="btn-primary text-sm no-underline">Join now</Link>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Page content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <Outlet />
      </main>
    </div>
  )
}
